import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { updateDrawingElement } from 'features/Procedure/state/procedure.slice'
import { DrawingElementVisibleState } from 'features/Procedure/state/procedure.type'
import {
  getProcedureDrawingElements,
  getSortedUndoableElements,
  getVisibleDrawingElements,
} from 'features/Procedure/utils/drawingElements.utils'

export const useDrawingHistory = (procedureId?: number | null) => {
  const dispatch = useAppDispatch()
  const drawingElements = useAppSelector((state) => state.procedureReducer.drawingElements)

  const visibleElements = useMemo(
    () => getVisibleDrawingElements(drawingElements),
    [drawingElements],
  )

  const procedureDrawingElements = useMemo(
    () => getProcedureDrawingElements(drawingElements, procedureId),
    [drawingElements, procedureId],
  )

  const sortedUndoableElements = useMemo(
    () => getSortedUndoableElements(procedureDrawingElements),
    [procedureDrawingElements],
  )

  const canUndo = useMemo(() => {
    return sortedUndoableElements.some(
      (el) => el.visibleState === DrawingElementVisibleState.VISIBLE,
    )
  }, [sortedUndoableElements])

  const canRedo = useMemo(() => {
    return sortedUndoableElements.some(
      (el) => el.visibleState === DrawingElementVisibleState.HIDDEN,
    )
  }, [sortedUndoableElements])

  const undo = useCallback(() => {
    const visibleUndoableElements = sortedUndoableElements.filter(
      (el) => el.visibleState === DrawingElementVisibleState.VISIBLE,
    )
    const lastVisibleElement = visibleUndoableElements[visibleUndoableElements.length - 1]

    if (lastVisibleElement) {
      dispatch(
        updateDrawingElement({
          id: lastVisibleElement.clientKey || lastVisibleElement.id,
          updates: { visibleState: DrawingElementVisibleState.HIDDEN },
        }),
      )
    }
  }, [sortedUndoableElements, dispatch])

  const redo = useCallback(() => {
    const visibleUndoableElements = sortedUndoableElements.filter(
      (el) => el.visibleState === DrawingElementVisibleState.VISIBLE,
    )
    const lastVisibleTimestamp =
      visibleUndoableElements[visibleUndoableElements.length - 1]?.timestamp || 0

    const nextHiddenElement = sortedUndoableElements.find(
      (el) =>
        el.visibleState === DrawingElementVisibleState.HIDDEN &&
        el.timestamp > lastVisibleTimestamp,
    )

    if (nextHiddenElement) {
      dispatch(
        updateDrawingElement({
          id: nextHiddenElement.clientKey || nextHiddenElement.id,
          updates: { visibleState: DrawingElementVisibleState.VISIBLE },
        }),
      )
    }
  }, [sortedUndoableElements, dispatch])

  return {
    visibleElements,
    canUndo,
    canRedo,
    undo,
    redo,
  }
}
